import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Smartphone, Zap, ArrowRight } from "lucide-react";
import { Generator } from "@/components/Generator";
import { AdSlot } from "@/components/AdSlot";
import { Faq } from "@/components/Faq";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getSeoPages } from "@/lib/api";

const GENERAL_FAQ = [
  { q: "Wie funktioniert der Generator?", a: "Du lädst 1 bis 2 Fotos hoch, gibst kurz an was du verkaufst und beantwortest bis zu 3 Fragen. Danach erhältst du einen fertigen Verkaufstext zum Kopieren." },
  { q: "Ist das Tool kostenlos?", a: "Ja. Du kannst ohne Login bis zu 3 Texte pro Tag erstellen." },
  { q: "Werden meine Fotos gespeichert?", a: "Nein. Die Originalbilder werden nicht dauerhaft gespeichert. Sie werden nur zur Erstellung des Textes verwendet." },
  { q: "Für welche Plattformen eignet sich der Text?", a: "Für Kleinanzeigen, Facebook Marketplace, Vinted und ähnliche Plattformen. Du bekommst passende Varianten." },
  { q: "Erfindet die KI Eigenschaften?", a: "Nein. Der Text bleibt ehrlich und nutzt nur deine Angaben und sichtbare Hinweise. Bei Unsicherheit wird vorsichtig formuliert." },
];

export default function HomePage() {
  const [pages, setPages] = useState([]);

  useEffect(() => {
    getSeoPages().then(setPages).catch(() => {});
  }, []);

  const groups = pages.reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p);
    return acc;
  }, {});

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "KleinanzeigenGenerator",
      url: origin || undefined,
      inLanguage: "de-DE",
      description: "Kleinanzeigen-Text aus Foto erstellen: kostenloser Generator für Verkaufstexte.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "KleinanzeigenGenerator",
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Web",
      url: origin || undefined,
      inLanguage: "de-DE",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      description: "Erstelle aus einem Produktfoto und wenigen Angaben einen fertigen Verkaufstext für Kleinanzeigen, Facebook Marketplace und Vinted.",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: GENERAL_FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div>
      <Seo
        title="Kleinanzeigen-Text aus Foto erstellen: kostenloser Generator"
        description="Lade ein Produktfoto hoch, gib kurz an, was du verkaufst, und erstelle einen fertigen Beschreibungstext für Kleinanzeigen, Facebook Marketplace, Vinted und Co."
      canonical={origin ? `${origin}${typeof window !== "undefined" ? window.location.pathname : "/"}` : undefined}
      />
      <JsonLd data={jsonLd} />

      {/* Hero + Generator */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-0" style={{ background: "linear-gradient(135deg, rgba(15,118,110,0.10), rgba(249,115,22,0.08), rgba(251,250,247,0))", height: "60%" }} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-6 relative">
          <div className="lg:grid lg:grid-cols-[1fr_320px] lg:gap-8">
            <div>
              <div className="max-w-2xl">
                <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">Kleinanzeigen-Text aus Foto erstellen</h1>
                <p className="mt-4 text-base sm:text-lg text-muted-foreground">
                  Foto hochladen, kurz sagen was du verkaufst, bis zu 3 Fragen beantworten, und du erhältst einen fertigen Beschreibungstext für Kleinanzeigen, Facebook Marketplace, Vinted und Co.
                </p>
                <div className="mt-5 flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Ohne Login</span>
                  <span className="inline-flex items-center gap-1.5"><Smartphone className="h-4 w-4 text-primary" /> Mobil optimiert</span>
                  <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-primary" /> In unter einer Minute</span>
                </div>
              </div>

              <div className="mt-6 lg:mt-8">
                <Generator />
              </div>
            </div>

            {/* Desktop ad rail */}
            <div className="hidden lg:block">
              <AdSlot variant="rail" />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile inline ad */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:hidden mt-4">
        <AdSlot variant="inline" />
      </div>

      {/* Categories */}
      <section id="kategorien" className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-xl sm:text-2xl font-semibold">Beliebte Kategorien</h2>
        <p className="text-muted-foreground mt-1">Wähle eine Kategorie für passende Fragen und Beispiele.</p>
        <div className="mt-6 space-y-8">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">{group}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((p) => (
                  <Link key={p.slug} to={p.urlPath} data-testid={`category-link-${p.slug}`}>
                    <Card className="p-3 h-full hover:shadow-md transition-shadow border flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{p.name}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { n: "1", t: "Foto hochladen", d: "Lade 1 bis 2 Produktfotos hoch, direkt vom Handy." },
            { n: "2", t: "Kurz beschreiben", d: "Gib an, was du verkaufst und beantworte bis zu 3 Fragen." },
            { n: "3", t: "Text kopieren", d: "Kopiere den fertigen Text für deine Plattform." },
          ].map((s) => (
            <Card key={s.n} className="p-5 border">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-heading">{s.n}</span>
              <p className="font-heading font-semibold mt-3">{s.t}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Crosslink */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6">
        <Card className="p-5 border flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm text-muted-foreground flex-1">Verkaufst du ein Sofa? Prüfe vorher, ob es durch Tür und Treppenhaus passt.</p>
          <a href="https://passt-das-durch.de" className="text-sm font-medium hover:underline shrink-0">Zum Durchgangsrechner</a>
        </Card>
      </div>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-8">
        <h2 className="text-xl sm:text-2xl font-semibold mb-4">Häufige Fragen</h2>
        <Faq items={GENERAL_FAQ} testId="home-faq-accordion" />
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <AdSlot variant="inline" />
      </div>
    </div>
  );
}
